// A member's full reads are budgeted (api/src/protection/limits.ts): a person reading stays far
// below the budget, an importer walking a creator's library goes over it within seconds. These
// helpers let a post page answer an over-budget read with the site's own calm page.

/**
 * Set on a page whose read went over the member's budget, with the seconds to wait. The Worker
 * answers such a page 429 with Retry-After (site/src/creator-page.ts), so a script that walks the
 * library sees the limit while the member sees the site's own page.
 */
export const readLimitHeader = "X-Drate-Read-Limit";

/**
 * Seconds to wait when a read failed for going over the member's budget (the service's
 * ORPCError "TOO_MANY_REQUESTS" with `data.retryAfter`), or null for any other failure. Matched by
 * its code rather than its class, so a second copy of the oRPC package can't hide it.
 */
export function readLimitRetryAfter(error: unknown): number | null {
  if (!(error instanceof Error) || (error as { code?: unknown }).code !== "TOO_MANY_REQUESTS")
    return null;
  const seconds = Number((error as { data?: { retryAfter?: unknown } }).data?.retryAfter);
  return Number.isFinite(seconds) && seconds > 0 ? Math.min(Math.ceil(seconds), 3600) : 60;
}

/** The wait in words for "Try again in …": "30 seconds", "a minute", "5 minutes". */
export function readLimitWait(seconds: number) {
  const whole = Math.max(1, Math.ceil(seconds));
  if (whole > 60) return `${Math.ceil(whole / 60)} minutes`;
  if (whole === 60) return "a minute";
  return whole === 1 ? "1 second" : `${whole} seconds`;
}
